import { Link } from "react-router-dom"
import Seo from "@/components/Seo"
import { COURSES } from "@/data/courses"

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: COURSES.map((c, i) => ({ "@type": "ListItem", position: i + 1, url: `https://www.jero-onetechnologies.com/training/${c.slug}`, name: c.title })),
}

const DELIVERY = [
  {
    title: "Corporate Training Programs",
    description:
      "Instructor-led courses with customizable content, delivered by experienced faculty at our centre or on your site.",
  },
  {
    title: "Project-Based Training",
    description:
      "Teams learn by building real application modules from their own work environment with Jero-One guidance.",
  },
  {
    title: "Technology-Based Training",
    description:
      "Flexible, self-paced computer-based multimedia courses that complement instructor-led learning.",
  },
  {
    title: "Corporate Learning Centres",
    description:
      "Dedicated learning centres established at your organization for continuous, scalable staff development.",
  },
]


export default function Training() {
  return (
    <div>
      <Seo title="IT Training Courses in Kampala, Uganda | Jero-One Academy" description="Practical IT training in Kampala & Entebbe: networking, cybersecurity, servers, cloud & AI, CCTV, Microsoft Office and software development. Corporate & individual courses." path="/training" schema={SCHEMA} />
      <section className="grid min-h-[62vh] bg-brand-navy lg:grid-cols-2">
        <div className="flex flex-col justify-center px-8 py-20 md:px-16">
          <div className="mb-4 font-mono text-xs tracking-widest text-brand-green-light">
            JERO-ONE ACADEMY
          </div>
          <h1 className="font-display text-6xl font-black leading-none text-brand-light md:text-7xl">
            BUILD SKILLS.
            <br />
            <span className="text-brand-green-light">DELIVER RESULTS.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-brand-muted">
            Practical IT training for individuals, teams, schools, and
            enterprises—delivered on-site, remotely, or through a dedicated
            corporate learning centre.
          </p>
          <Link
            to="/contact"
            className="mt-8 self-start rounded-xl bg-brand-green px-8 py-4 text-sm font-bold text-brand-light transition-colors hover:bg-brand-green-light"
          >
            Request a training plan
          </Link>
        </div>
        <div className="relative min-h-80 overflow-hidden">
          <img fetchPriority="high"
            src="/website-assets/images/photo-1651608671506-4d57dfd57ee5-1400x1000.jpg"
            alt="Professionals learning together with laptops"
            className="h-full w-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-transparent to-brand-navy/30" />
        </div>
      </section>

      <section className="bg-[#0A0F0B] px-6 py-20 md:px-14">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-2xl">
            <div className="mb-3 font-mono text-xs tracking-widest text-brand-green-light">
              LEARN YOUR WAY
            </div>
            <h2 className="font-display text-5xl font-black text-brand-light">
              FOUR DELIVERY MODELS
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {DELIVERY.map((item, index) => (
              <article
                key={item.title}
                className="rounded-2xl border border-brand-green/15 bg-brand-mid p-8 transition-colors hover:border-brand-green/50"
              >
                <div className="mb-6 font-mono text-xs text-brand-green-light">
                  0{index + 1}
                </div>
                <h3 className="font-display text-3xl font-bold text-brand-light">
                  {item.title}
                </h3>
                <p className="mt-3 leading-relaxed text-brand-muted">
                  {item.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-mid px-6 py-20 md:px-14">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <div className="mb-3 font-mono text-xs tracking-widest text-brand-green-light">
              CURRENT PROGRAMS
            </div>
            <h2 className="font-display text-5xl font-black text-brand-light">
              PRACTICAL IT COURSES
            </h2>
          </div>
          <div className="overflow-hidden rounded-2xl border border-brand-green/15">
            {COURSES.map(({ slug, title, track, level, duration }) => (
              <div
                key={title}
                className="grid gap-3 border-b border-brand-green/10 bg-[#0A0F0B] p-6 last:border-0 md:grid-cols-[1.5fr_1fr_1fr_auto] md:items-center"
              >
                <div>
                  <Link to={`/training/${slug}`} className="font-bold text-brand-light hover:text-brand-green-light">{title}</Link>
                  <div className="mt-1 text-xs text-brand-green-light">
                    {track}
                  </div>
                </div>
                <div className="text-sm text-brand-muted">{level}</div>
                <div className="text-sm text-brand-muted">{duration}</div>
                <Link
                  to={`/training/${slug}`}
                  data-testid={`course-link-${slug}`}
                  className="text-sm font-semibold text-brand-green-light hover:underline"
                >
                  Course details →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-green px-6 py-16 md:px-14">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="font-display text-5xl font-black text-brand-light">
            TRAIN YOUR WHOLE TEAM
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-brand-light/70">
            We tailor course content, scheduling, and delivery to your
            technology stack and business goals.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-block rounded-xl bg-brand-light px-10 py-4 font-bold text-brand-green"
          >
            Build a custom program
          </Link>
        </div>
      </section>
    </div>
  )
}
